import type { CardAttachment, GoogleClassroomPostType } from "@shared/models/card-stack"
import { getSliceText } from "@shared/unified-tiptap/util/get-text"
import { objectId } from "@shared/util/object-id"
import {
  CardGoogleClassroomContentExtension,
  type ExtensionCardGoogleClassroomAttrs,
} from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { ExtensionCardTitleAttrs } from "@tiptap-content/extensions/card-title/card-title-content-extension"
import type { CardExtensionAttrs } from "@tiptap-content/extensions/cards/card-content-extensions"
import type { Link } from "@tiptap-content/extensions/link/link-attrs"
import type { Editor } from "@tiptap/core"
import { Fragment, Slice, type Node as PMNode } from "@tiptap/pm/model"
import { Plugin } from "@tiptap/pm/state"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { CardGoogleClassroomComponent } from "./card-google-classroom-component"

/**
 * A Google Classroom card. Mostly non-interactive in the editor itself: the node view
 * displays the post (title, value, attachments) and clicking it opens the Google
 * Classroom sidebar, where the actual editing happens.
 *
 * Group "card" makes it a sibling of titledCard at the lesson level; "widget" opts into
 * the editor's size-1 ReactNodeView handling (click overrides, comment decorations —
 * see CLAUDE.md), though unlike other widgets it is not in group "block", so it can
 * never appear inside a card.
 */
export const ExtensionCardGoogleClassroom = CardGoogleClassroomContentExtension.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CardGoogleClassroomComponent)
  },

  addProseMirrorPlugins() {
    return [new Plugin({ props: { transformPasted } })]
  },
})

/**
 * The Google Classroom card a titledCard becomes when posted (see `postCardToGoogleClassroom`).
 *
 * The card keeps everything it already had — its id (comments and the backend's Google
 * Classroom post are keyed to it), title, comment ids, color, and public flag — while the
 * post-specific attrs start at the post type's defaults. A Google Classroom post holds
 * plaintext and a list of attachments, so the card's block content is flattened: the
 * attachment tokens (linkBlocks) become `attachments`, and what is left becomes `value`.
 */
export function googleClassroomCardFromTitledCard(
  editor: Editor,
  card: PMNode,
  postType: GoogleClassroomPostType,
): PMNode {
  const cardAttrs = card.attrs as CardExtensionAttrs
  const title = card.firstChild
  const titleAttrs = title?.attrs as ExtensionCardTitleAttrs | undefined

  const content = card.children.slice(1)
  const attachments = content
    .filter((node) => node.type.name === "linkBlock")
    .map((node) => cardAttachmentFromLink(node.attrs as Link))
  const valueContent = content.filter((node) => node.type.name !== "linkBlock")

  return editor.schema.node("cardGoogleClassroom", {
    ...cardAttrs,
    color: titleAttrs?.color ?? "yellow",
    postType,
    title: title?.textContent ?? "",
    value: getSliceText(editor, new Slice(Fragment.fromArray(valueContent), 0, 0)),
    maxPoints: null,
    classSettings: [],
    attachmentSettings: {},
    // Multiple choice can't be posted without at least one choice, so seed one.
    extraPostProperties: postType === "MULTIPLE_CHOICE_QUESTION" ? { choices: ["Option 1"] } : {},
    enabledGoogleClassIds: [],
    attachments,
    revision: 0,
  } satisfies ExtensionCardGoogleClassroomAttrs)
}

/**
 * Converts unified Link attrs back to the legacy CardAttachment shape (the reverse of
 * the docs/unified-links.md §10 mapping): attachmentId → id, minting one if absent.
 */
export function cardAttachmentFromLink(link: Link): CardAttachment {
  return {
    id: link.attachmentId ?? objectId(),
    title: link.title ?? link.url,
    url: link.url,
    type: link.type,
    mimetype: link.mimetype,
    size: link.size,
    isWriteable: link.isWriteable,
  }
}

/**
 * When pasting cardGoogleClassroom cards, assign each a fresh id and store the original
 * as parentCardId.
 * Also erase commentIds, to avoid duplicating comments.
 * Cf. card-extensions' transformPasted.
 */
function transformPasted(slice: Slice): Slice {
  const replaceCards = (fragment: Fragment): Fragment => {
    const children: ReturnType<typeof fragment.child>[] = []
    for (let i = 0; i < fragment.childCount; i++) {
      const node = fragment.child(i)
      if (node.type.name === "cardGoogleClassroom") {
        const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
        const newAttrs = {
          ...attrs,
          id: objectId(),
          parentCardId: attrs.id,
          commentIds: [],
        } satisfies ExtensionCardGoogleClassroomAttrs
        children.push(node.type.create(newAttrs, replaceCards(node.content), node.marks))
      } else {
        children.push(node.copy(replaceCards(node.content)))
      }
    }
    return Fragment.fromArray(children)
  }

  return new Slice(replaceCards(slice.content), slice.openStart, slice.openEnd)
}
